import { useMemo } from "react";
import flatten from "lodash/flatten";
import type { UseInfiniteQueryOptions } from "react-query";
import { useInfiniteQuery } from "react-query";
import { useWorkflowsApi } from "../api/useWorkflowsApi";
import type {
  AudienceFilters,
  ListWorkflows,
} from "../components/SettingsApp/Workflows/generated/WorkflowsApi.types";
import type { Params } from "../helpers/urlHelpers";

export const SORT_OPTIONS_MAP = {
  ALPHABETICAL: "alphabetical",
  OLDEST: "oldest",
  LATEST: "latest",
} as const;

export const DEFAULT_PAGE_SIZE = 20 as const;

interface UseAllWorkflowsInfiniteQueryArgs {
  filters?: AudienceFilters["filters"];
  initialPage?: number;
  options?: UseInfiniteQueryOptions<ListWorkflows>;
  perPage?: number;
  query?: string;
  sort?: (typeof SORT_OPTIONS_MAP)[keyof typeof SORT_OPTIONS_MAP];
  status?: string;
  workflowType?: string;
}

export const useAllWorkflowsInfiniteQuery = (
  args: UseAllWorkflowsInfiniteQueryArgs = {},
) => {
  const {
    query = "",
    sort = SORT_OPTIONS_MAP.ALPHABETICAL,
    filters = {},
    perPage = DEFAULT_PAGE_SIZE,
    status,
    workflowType,
    initialPage = 1,
    options = {},
  } = args;

  const workflowsApi = useWorkflowsApi();

  const fetchPage = ({ pageParam = initialPage }) => {
    let page = 1;

    if (typeof pageParam === "number" && pageParam > 0) {
      page = pageParam;
    }

    const params: Params = {
      page,
      sort,
      filters,
      status,
      workflow_type: workflowType,
      per_page: perPage,
    };

    if (query) {
      params.query = query;
    }

    return workflowsApi.list({ params });
  };

  const queryKey = [
    "all-workflows",
    query,
    sort,
    filters,
    perPage,
    status,
    workflowType,
    initialPage,
  ];

  /* TODO: Make abstract */
  const queryOptions = {
    getNextPageParam: (currentPage: ListWorkflows) =>
      currentPage.has_next_page && currentPage.page
        ? currentPage.page + 1
        : undefined,
    getPreviousPageParam: (currentPage: ListWorkflows) =>
      currentPage.page && currentPage.page > 1
        ? currentPage.page - 1
        : undefined,
    keepPreviousData: true,
    ...options,
  };

  /* TODO: Make abstract */
  const result = useInfiniteQuery<ListWorkflows>(
    queryKey,
    fetchPage,
    queryOptions,
  );
  const pages = useMemo(() => result.data?.pages || [], [result.data?.pages]);
  const totalWorkflows = pages[0]?.total_count || 0;
  const pageSize = pages[0]?.per_page || perPage;
  const workflows = useMemo(
    () => flatten(pages.map(page => page.records)),
    [pages],
  );

  return {
    ...result,
    queryKey,
    totalWorkflows,
    workflows,
    pageSize,
    pages,
  };
};
